import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "./cx";
import { clinicalState, token, type ClinicalStatus } from "@/design/theme";

export type BadgeTone = "neutral" | "success" | "warn" | "danger" | "info" | ClinicalStatus;

const TONE_STYLE: Record<BadgeTone, { color: string; bg: string }> = {
  neutral: { color: token.slate, bg: token.surface2 },
  success: { color: token.success, bg: token.tintSuccess },
  warn: { color: token.warn, bg: token.tintHigh },
  danger: { color: token.danger, bg: token.tintCritical },
  info: { color: token.info, bg: token.azureSoft },
  normal: { color: token.normal, bg: token.tintSuccess },
  low: { color: token.low, bg: token.tintLow },
  high: { color: token.high, bg: token.tintHigh },
  critical: { color: token.critical, bg: token.tintCritical },
  hold: { color: token.hold, bg: token.tintHold },
};

const CLINICAL_TONES: ReadonlySet<string> = new Set<BadgeTone>([
  "normal",
  "low",
  "high",
  "critical",
  "hold",
]);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Force a glyph; clinical tones auto-supply their mandated shape glyph. */
  glyph?: ReactNode;
  children: ReactNode;
}

/**
 * Badge — chip with color + (for clinical tones) the MANDATED shape glyph + text. Clinical states
 * MUST never be color-only (daltonisme + médico-légal): when `tone` is a clinical status the glyph
 * is injected automatically.
 */
export function Badge({ tone = "neutral", glyph, children, className, style, ...rest }: BadgeProps) {
  const s = TONE_STYLE[tone];
  const autoGlyph =
    glyph ?? (CLINICAL_TONES.has(tone) ? clinicalState(tone as ClinicalStatus).glyph : null);
  return (
    <span
      className={cx("nv-badge", className)}
      style={{ color: s.color, background: s.bg, ...style }}
      {...rest}
    >
      {autoGlyph && (
        <span className="nv-badge__glyph" aria-hidden>
          {autoGlyph}
        </span>
      )}
      {children}
    </span>
  );
}
